import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import { Provider } from '@dataTypes/provider.types'
import { IPhases } from '@dataTypes/treatmentPlan.types'
import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'
import { createFailedToast } from '@utils/notificationToast.util'
import { getFullName } from '@utils/fullname.util'
import {
  applyTxPlanProviderToEmptyProcedures,
  applyTxPlanProviderToVisibleProcedures,
  clearTxPlanVisibleProcedureProviders,
  countTxPlanEmptyProviderProcedures,
  countTxPlanProviderAssignments,
  txPlanPhaseHeaderKey,
  txPlanProcedureProviderHighlightRowKey,
  updateTxPlanProcedureProviderOnPhase,
} from '@utils/treatment-plan.utils'
import { getTxPlanSaveProviderValidation } from './treatmentPlanMain.helpers'
import type {
  ClearPlanProvidersModal,
  PendingBulkProvider,
  PlanProviderAssignModal,
  TxPlanProviderMenuAvailability,
  TxPlanTabFormState,
} from './TreatmentPlanMain.types'
import type { TxPlanProcedureProviderChangeArgs } from './TxPlanPhaseProcedureGrid.component'

export type UseTreatmentPlanProvidersParams = {
  activeTabKey: string
  localDrafts: Record<string, boolean>
  editModes: Record<string, boolean>
  formStates: Record<string, TxPlanTabFormState>
  formStatesRef: MutableRefObject<Record<string, TxPlanTabFormState>>
  providerList: Provider[]
  staffList: Provider[]
  setFormStates: Dispatch<SetStateAction<Record<string, TxPlanTabFormState>>>
  queueHistorySnapshotBefore: (tabKey: string) => void
  markHistorySavePending: (tabKey: string) => void
  showToast: (toast: NotificationToastData) => void
}

export type UseTreatmentPlanProvidersResult = {
  missingProviderRowKeysByTab: Record<string, Set<string>>
  phaseValidationErrorKeysByTab: Record<string, Set<string>>
  pendingBulkProvider: PendingBulkProvider | null
  bulkProviderDontAskAgain: boolean
  setBulkProviderDontAskAgain: Dispatch<SetStateAction<boolean>>
  planProviderAssignModal: PlanProviderAssignModal | null
  clearPlanProvidersModal: ClearPlanProvidersModal | null
  menuFillProviderId: string
  setMenuFillProviderId: Dispatch<SetStateAction<string>>
  clearMissingProviderRowHighlight: (planTabKey: string, rowKey: string) => void
  clearPhaseValidationError: (planTabKey: string, phaseKey: string) => void
  clearPhaseValidationErrorsForTab: (planTabKey: string) => void
  clearProviderValidationForTab: (planTabKey: string) => void
  resetAllProviderState: () => void
  handleProcedureProviderChange: (
    args: TxPlanProcedureProviderChangeArgs,
  ) => void
  providerMenuDisabled: TxPlanProviderMenuAvailability
  handleOpenAssignMissingProviders: (tabKey: string) => void
  handleOpenReplaceAllProviders: (tabKey: string) => void
  handleOpenClearAllProviders: (tabKey: string) => void
  closePlanProviderAssignModal: () => void
  confirmPlanProviderAssign: () => void
  closeClearPlanProvidersModal: () => void
  confirmClearPlanProviders: () => void
  handleBulkProviderModalHide: () => void
  handleBulkProviderOnlyThisProcedure: () => void
  confirmBulkProviderApplyAll: () => void
  validateProvidersAndPhasesBeforeSave: (
    tabKey: string,
    phases: IPhases[],
  ) => boolean
}

function deleteTabKeyFromSetRecord(
  prev: Record<string, Set<string>>,
  tabKey: string,
): Record<string, Set<string>> {
  if (!prev[tabKey]) return prev
  const next = { ...prev }
  delete next[tabKey]
  return next
}

export function useTreatmentPlanProviders({
  activeTabKey,
  localDrafts,
  editModes,
  formStates,
  formStatesRef,
  providerList,
  staffList,
  setFormStates,
  queueHistorySnapshotBefore,
  markHistorySavePending,
  showToast,
}: UseTreatmentPlanProvidersParams): UseTreatmentPlanProvidersResult {
  const [missingProviderRowKeysByTab, setMissingProviderRowKeysByTab] =
    useState<Record<string, Set<string>>>({})
  const [phaseValidationErrorKeysByTab, setPhaseValidationErrorKeysByTab] =
    useState<Record<string, Set<string>>>({})
  const [pendingBulkProvider, setPendingBulkProvider] =
    useState<PendingBulkProvider | null>(null)
  const [bulkProviderDontAskAgain, setBulkProviderDontAskAgain] =
    useState(false)
  const [planProviderAssignModal, setPlanProviderAssignModal] =
    useState<PlanProviderAssignModal | null>(null)
  const [clearPlanProvidersModal, setClearPlanProvidersModal] =
    useState<ClearPlanProvidersModal | null>(null)
  const [menuFillProviderId, setMenuFillProviderId] = useState('')
  const skipBulkProviderPromptRef = useRef<Set<string>>(new Set())
  const bulkProviderDontAskAgainRef = useRef(false)
  const bulkProviderFooterDismissRef = useRef(false)
  bulkProviderDontAskAgainRef.current = bulkProviderDontAskAgain

  const clearMissingProviderRowHighlight = useCallback(
    (planTabKey: string, rowKey: string): void => {
      setMissingProviderRowKeysByTab((prev) => {
        const set = prev[planTabKey]
        if (!set?.has(rowKey)) return prev
        const nextSet = new Set(set)
        nextSet.delete(rowKey)
        const next = { ...prev }
        if (nextSet.size === 0) {
          delete next[planTabKey]
        } else {
          next[planTabKey] = nextSet
        }
        return next
      })
    },
    [],
  )

  const clearPhaseValidationError = useCallback(
    (planTabKey: string, phaseKey: string): void => {
      setPhaseValidationErrorKeysByTab((prev) => {
        const set = prev[planTabKey]
        if (!set?.has(phaseKey)) return prev
        const nextSet = new Set(set)
        nextSet.delete(phaseKey)
        const next = { ...prev }
        if (nextSet.size === 0) {
          delete next[planTabKey]
        } else {
          next[planTabKey] = nextSet
        }
        return next
      })
    },
    [],
  )

  const clearPhaseValidationErrorsForTab = useCallback(
    (planTabKey: string): void => {
      setPhaseValidationErrorKeysByTab((prev) => {
        if (!prev[planTabKey]) return prev
        const next = { ...prev }
        delete next[planTabKey]
        return next
      })
    },
    [],
  )

  const clearProviderValidationForTab = useCallback(
    (planTabKey: string): void => {
      setMissingProviderRowKeysByTab((prev) =>
        deleteTabKeyFromSetRecord(prev, planTabKey),
      )
      clearPhaseValidationErrorsForTab(planTabKey)
    },
    [clearPhaseValidationErrorsForTab],
  )

  const resetAllProviderState = useCallback((): void => {
    setMissingProviderRowKeysByTab({})
    setPhaseValidationErrorKeysByTab({})
    setPendingBulkProvider(null)
    setBulkProviderDontAskAgain(false)
    setPlanProviderAssignModal(null)
    setClearPlanProvidersModal(null)
    setMenuFillProviderId('')
    skipBulkProviderPromptRef.current = new Set()
  }, [])

  useEffect(() => {
    skipBulkProviderPromptRef.current = new Set()
  }, [activeTabKey])

  useEffect(() => {
    const skipSet = skipBulkProviderPromptRef.current
    Object.keys(editModes).forEach((tabKey) => {
      const isLocalDraft = localDrafts[tabKey] || false
      const isEditMode = isLocalDraft || editModes[tabKey] || false
      if (!isEditMode) {
        skipSet.delete(tabKey)
      }
    })
  }, [editModes, localDrafts])

  const closeBulkProviderModal = useCallback(
    (suppressFuturePrompts: boolean): void => {
      setPendingBulkProvider((current) => {
        if (!current) return null
        if (suppressFuturePrompts) {
          skipBulkProviderPromptRef.current.add(current.tabKey)
        } else {
          skipBulkProviderPromptRef.current.delete(current.tabKey)
        }
        return null
      })
      setBulkProviderDontAskAgain(false)
    },
    [],
  )

  const handleBulkProviderOnlyThisProcedure = useCallback((): void => {
    bulkProviderFooterDismissRef.current = true
    closeBulkProviderModal(bulkProviderDontAskAgainRef.current)
  }, [closeBulkProviderModal])

  const handleBulkProviderModalHide = useCallback((): void => {
    if (bulkProviderFooterDismissRef.current) {
      bulkProviderFooterDismissRef.current = false
      return
    }
    closeBulkProviderModal(bulkProviderDontAskAgainRef.current)
  }, [closeBulkProviderModal])

  const handleProcedureProviderChange = useCallback(
    (args: TxPlanProcedureProviderChangeArgs): void => {
      const { tabKey, phaseOriginalIdx, item, providerId } = args
      const currentProviderId = item.provider_id ?? ''
      if (currentProviderId === providerId) {
        return
      }
      const prevState = formStatesRef.current[tabKey]
      if (!prevState) {
        return
      }
      const rowKey = txPlanProcedureProviderHighlightRowKey(
        phaseOriginalIdx,
        item,
      )
      if (providerId.trim() !== '') {
        clearMissingProviderRowHighlight(tabKey, rowKey)
      }
      const updatedPhases = updateTxPlanProcedureProviderOnPhase(
        prevState.phases,
        phaseOriginalIdx,
        item,
        providerId,
      )
      queueHistorySnapshotBefore(tabKey)
      setFormStates((prev) => {
        const state = prev[tabKey]
        if (!state) return prev
        return {
          ...prev,
          [tabKey]: {
            ...state,
            phases: updatedPhases,
          },
        }
      })
      markHistorySavePending(tabKey)
      const updatedPhase = updatedPhases[phaseOriginalIdx]
      if (updatedPhase !== undefined) {
        const phaseKey = txPlanPhaseHeaderKey(updatedPhase, phaseOriginalIdx)
        const phaseStillMissingProvider = updatedPhase.items.some(
          (phaseItem) => {
            if (phaseItem.is_hidden) return false
            return (phaseItem.provider_id?.trim() ?? '') === ''
          },
        )
        if (!phaseStillMissingProvider) {
          clearPhaseValidationError(tabKey, phaseKey)
        }
      }
      if (providerId.trim() === '') {
        return
      }
      const otherEmptyCount = countTxPlanEmptyProviderProcedures(updatedPhases)
      const provider = staffList.find((p) => p.provider_id === providerId)
      const providerDisplayName =
        provider !== undefined ? getFullName(provider) : 'Selected provider'
      if (
        otherEmptyCount >= 1 &&
        !skipBulkProviderPromptRef.current.has(tabKey)
      ) {
        bulkProviderFooterDismissRef.current = false
        setBulkProviderDontAskAgain(false)
        setPendingBulkProvider({
          tabKey,
          providerId,
          providerDisplayName,
          emptyCount: otherEmptyCount,
          phaseOriginalIdx,
          item,
        })
      }
    },
    [
      clearMissingProviderRowHighlight,
      clearPhaseValidationError,
      formStatesRef,
      markHistorySavePending,
      queueHistorySnapshotBefore,
      setFormStates,
      staffList,
    ],
  )

  const providerMenuDisabled = useMemo((): TxPlanProviderMenuAvailability => {
    const disabled: TxPlanProviderMenuAvailability = {
      assignMissingDisabled: true,
      replaceAllDisabled: true,
      clearAllDisabled: true,
    }
    const isLocalDraft = localDrafts[activeTabKey] || false
    const isEditMode = isLocalDraft || editModes[activeTabKey] || false
    if (!isEditMode) {
      return disabled
    }
    const formState = formStates[activeTabKey]
    if (!formState) {
      return disabled
    }
    const counts = countTxPlanProviderAssignments(formState.phases)
    const hasProviders = providerList.length > 0
    return {
      assignMissingDisabled: !hasProviders || counts.empty === 0,
      replaceAllDisabled: !hasProviders || counts.visible === 0,
      clearAllDisabled: counts.assigned === 0,
    }
  }, [activeTabKey, editModes, formStates, localDrafts, providerList.length])

  const handleOpenAssignMissingProviders = useCallback(
    (tabKey: string): void => {
      const prevState = formStates[tabKey]
      if (!prevState) {
        return
      }
      const emptyCount = countTxPlanEmptyProviderProcedures(prevState.phases)
      if (emptyCount === 0) {
        return
      }
      setMenuFillProviderId('')
      setPlanProviderAssignModal({
        tabKey,
        mode: 'missing',
        affectedCount: emptyCount,
      })
    },
    [formStates],
  )

  const handleOpenReplaceAllProviders = useCallback(
    (tabKey: string): void => {
      const prevState = formStates[tabKey]
      if (!prevState || providerList.length === 0) {
        return
      }
      const visibleCount = countTxPlanProviderAssignments(
        prevState.phases,
      ).visible
      if (visibleCount === 0) {
        return
      }
      setMenuFillProviderId('')
      setPlanProviderAssignModal({
        tabKey,
        mode: 'replace',
        affectedCount: visibleCount,
      })
    },
    [formStates, providerList.length],
  )

  const handleOpenClearAllProviders = useCallback(
    (tabKey: string): void => {
      const prevState = formStates[tabKey]
      if (!prevState) {
        return
      }
      const assignedCount = countTxPlanProviderAssignments(
        prevState.phases,
      ).assigned
      if (assignedCount === 0) {
        return
      }
      setClearPlanProvidersModal({ tabKey, assignedCount })
    },
    [formStates],
  )

  const closePlanProviderAssignModal = useCallback((): void => {
    setPlanProviderAssignModal(null)
    setMenuFillProviderId('')
  }, [])

  const closeClearPlanProvidersModal = useCallback((): void => {
    setClearPlanProvidersModal(null)
  }, [])

  const confirmPlanProviderAssign = useCallback((): void => {
    if (!planProviderAssignModal || menuFillProviderId.trim() === '') {
      return
    }
    const { tabKey, mode } = planProviderAssignModal
    const providerId = menuFillProviderId.trim()
    queueHistorySnapshotBefore(tabKey)
    setFormStates((prev) => {
      const prevState = prev[tabKey]
      if (!prevState) return prev
      const phases =
        mode === 'replace'
          ? applyTxPlanProviderToVisibleProcedures(prevState.phases, providerId)
          : applyTxPlanProviderToEmptyProcedures(prevState.phases, providerId)
      return {
        ...prev,
        [tabKey]: {
          ...prevState,
          phases,
        },
      }
    })
    setMissingProviderRowKeysByTab((prev) =>
      deleteTabKeyFromSetRecord(prev, tabKey),
    )
    clearPhaseValidationErrorsForTab(tabKey)
    markHistorySavePending(tabKey)
    closePlanProviderAssignModal()
  }, [
    clearPhaseValidationErrorsForTab,
    closePlanProviderAssignModal,
    markHistorySavePending,
    menuFillProviderId,
    planProviderAssignModal,
    queueHistorySnapshotBefore,
    setFormStates,
  ])

  const confirmClearPlanProviders = useCallback((): void => {
    if (!clearPlanProvidersModal) {
      return
    }
    const { tabKey } = clearPlanProvidersModal
    queueHistorySnapshotBefore(tabKey)
    setFormStates((prev) => {
      const prevState = prev[tabKey]
      if (!prevState) return prev
      return {
        ...prev,
        [tabKey]: {
          ...prevState,
          phases: clearTxPlanVisibleProcedureProviders(prevState.phases),
        },
      }
    })
    markHistorySavePending(tabKey)
    closeClearPlanProvidersModal()
  }, [
    clearPlanProvidersModal,
    closeClearPlanProvidersModal,
    markHistorySavePending,
    queueHistorySnapshotBefore,
    setFormStates,
  ])

  const confirmBulkProviderApplyAll = useCallback((): void => {
    bulkProviderFooterDismissRef.current = true
    const suppressFuturePrompts = bulkProviderDontAskAgainRef.current
    setPendingBulkProvider((pending) => {
      if (!pending) return null
      const { tabKey, providerId } = pending
      if (suppressFuturePrompts) {
        skipBulkProviderPromptRef.current.add(tabKey)
      }
      setFormStates((prev) => {
        const prevState = prev[tabKey]
        if (!prevState) return prev
        return {
          ...prev,
          [tabKey]: {
            ...prevState,
            phases: applyTxPlanProviderToEmptyProcedures(
              prevState.phases,
              providerId,
            ),
          },
        }
      })
      setMissingProviderRowKeysByTab((prev) =>
        deleteTabKeyFromSetRecord(prev, tabKey),
      )
      clearPhaseValidationErrorsForTab(tabKey)
      return null
    })
    setBulkProviderDontAskAgain(false)
  }, [clearPhaseValidationErrorsForTab, setFormStates])

  const validateProvidersAndPhasesBeforeSave = useCallback(
    (tabKey: string, phases: IPhases[]): boolean => {
      const validation = getTxPlanSaveProviderValidation(phases)
      if (!validation.hasEmptyPhases && !validation.hasMissingProviders) {
        clearProviderValidationForTab(tabKey)
        return true
      }
      setPhaseValidationErrorKeysByTab((prev) => ({
        ...prev,
        [tabKey]: validation.phaseValidationKeys,
      }))
      if (validation.hasMissingProviders) {
        setMissingProviderRowKeysByTab((prev) => ({
          ...prev,
          [tabKey]: new Set(validation.missingProviderKeys),
        }))
      } else {
        setMissingProviderRowKeysByTab((prev) =>
          deleteTabKeyFromSetRecord(prev, tabKey),
        )
      }
      if (validation.hasEmptyPhases && validation.hasMissingProviders) {
        showToast(
          createFailedToast(
            'Cannot save treatment plan',
            'Add a procedure to every phase and choose a provider for each procedure before saving.',
          ),
        )
      } else if (validation.hasEmptyPhases) {
        showToast(
          createFailedToast(
            'Phase has no procedure',
            'Each phase must include at least one procedure. Add a procedure to every phase or remove empty phases before saving.',
          ),
        )
      } else {
        showToast(
          createFailedToast(
            'Missing provider',
            'One or more procedures do not have a provider selected. Choose a provider for each procedure before saving.',
          ),
        )
      }
      return false
    },
    [clearProviderValidationForTab, showToast],
  )

  return {
    missingProviderRowKeysByTab,
    phaseValidationErrorKeysByTab,
    pendingBulkProvider,
    bulkProviderDontAskAgain,
    setBulkProviderDontAskAgain,
    planProviderAssignModal,
    clearPlanProvidersModal,
    menuFillProviderId,
    setMenuFillProviderId,
    clearMissingProviderRowHighlight,
    clearPhaseValidationError,
    clearPhaseValidationErrorsForTab,
    clearProviderValidationForTab,
    resetAllProviderState,
    handleProcedureProviderChange,
    providerMenuDisabled,
    handleOpenAssignMissingProviders,
    handleOpenReplaceAllProviders,
    handleOpenClearAllProviders,
    closePlanProviderAssignModal,
    confirmPlanProviderAssign,
    closeClearPlanProvidersModal,
    confirmClearPlanProviders,
    handleBulkProviderModalHide,
    handleBulkProviderOnlyThisProcedure,
    confirmBulkProviderApplyAll,
    validateProvidersAndPhasesBeforeSave,
  }
}
